/**
 * Port of {@class IamCelSuggestionEngine} —
 * backend/src/main/java/com/irian/iam/ruleengine/cel/IamCelSuggestionEngine.java
 *
 * AST-driven suggestion engine for CEL expressions over the IAM rule schema.
 *
 * PLATFORM SUBSTITUTION:
 *   - cel-java's CelExpr / ExprKind.Kind → @bufbuild/cel's Expr / exprKind.case
 *   - cel-java's StructType / ListType / MapType → IamRuleExpressionSchema's
 *     own TypeRef (struct/list/map/scalar) since @bufbuild/cel uses a different
 *     type representation that we don't fully consume here.
 *   - cel-java's CELLexer (ANTLR tokens) → minimal hand-written token lexer in
 *     CelTokenView (sufficient for the suggester's token-only fast paths).
 *
 * Port is incremental — every chunk that lands drives the catalog runner pass
 * count higher. The current state:
 *   [x] expressionStartPool (empty source → all roots + stdlib + openers)
 *   [ ] staticAnswerFromTokens (token-only fast paths)
 *   [ ] partialOperatorCompletion
 *   [ ] suggestFromAst (AST-walking)
 *   [ ] applyExactMatchNextStep
 */

import {parse} from '@bufbuild/cel'
import {EMPTY_ENUM_VALUE_PROVIDER, type EnumValueProvider} from '../api/EnumValueProvider'
import type {FieldDefinition} from '../api/ExpressionFieldRegistry'
import {
    buildCursorContext,
    dottedPathOf,
    identName,
    type CursorContext,
} from './CursorContext'
import {IamCelCompiler} from './IamCelCompiler'
import {IamRuleExpressionSchema, isList, isMap, isScalar, isStruct, type StructType, type TypeRef} from './IamRuleExpressionSchema'
import {
    Kind,
    celSuggestion,
    type CelSuggestion,
} from './CelSuggestion'
import {
    CEL_LIST_METHODS,
    CEL_STDLIB,
    CEL_STRING_METHODS,
    CelSuggestionReplacements,
    classifyKind,
    CURSOR,
} from './CelSuggestionReplacements'

const ROOT_DESCRIPTIONS: ReadonlyMap<string, string> = new Map([
    ['identity', 'An identity — digital persona of a party'],
    ['party', 'A party — person, legal entity, team, device, etc.'],
    ['functionGroup', 'A bundle of access profiles assigned together'],
    ['accessProfile', 'Requestable access bundle'],
    ['partyAttributeDefinition', 'Definition row for a party dynamic attribute'],
    ['identityAttributeDefinition', 'Definition row for an identity dynamic attribute'],
    ['profile', 'Restriction-rule profile — code, name, riskLevel, id'],
])

const STDLIB_DESCRIPTIONS: ReadonlyMap<string, string> = new Map([
    ['size', 'size(x) — number of elements in a list/map or characters in a string'],
    ['int', 'int(x) — cast to integer'],
    ['uint', 'uint(x) — cast to unsigned integer'],
    ['double', 'double(x) — cast to floating-point'],
    ['string', 'string(x) — cast to string'],
    ['bool', 'bool(x) — cast to boolean'],
    ['bytes', 'bytes(x) — cast to bytes'],
    ['type', 'type(x) — runtime type of x'],
    ['timestamp', 'timestamp(rfc3339) — parse an RFC-3339 timestamp'],
    ['duration', "duration('1h30m') — parse an ISO-8601 duration"],
    ['dyn', 'dyn(x) — opt x out of static type checking'],
    ['has', 'has(field) — true when the field is present'],
    ['cel.bind', 'cel.bind(name, value, body) — bind a local name in body'],
])

const SYMBOL_DESCRIPTIONS: ReadonlyMap<string, string> = new Map([
    ['==', 'Equals'],
    ['!=', 'Not equals'],
    ['<', 'Less than'],
    ['<=', 'Less than or equal'],
    ['>', 'Greater than'],
    ['>=', 'Greater than or equal'],
    ['in', 'In list — true when left appears in the right list'],
    ['&&', 'Logical AND'],
    ['||', 'Logical OR'],
    ['!', 'Logical NOT'],
    ['(', 'Open parenthesis — group an expression'],
    [')', 'Close parenthesis'],
    [']', 'Close bracket'],
])

const UNARY_OPENERS: readonly string[] = ['!', '(']

const LIST_MACRO_DESCRIPTIONS: ReadonlyMap<string, string> = new Map([
    ['exists', 'exists(it, p) — at least one element matches'],
    ['all', 'all(it, p) — every element matches'],
    ['filter', 'filter(it, p) — sublist of elements that match'],
    ['map', 'map(it, e) — transform each element'],
    ['exists_one', 'exists_one(it, p) — exactly one element matches'],
    ['size', 'size() — number of elements'],
])

const STRING_METHOD_DESCRIPTIONS: ReadonlyMap<string, string> = new Map([
    ['startsWith', "startsWith('x') — string starts with 'x'"],
    ['endsWith', "endsWith('x') — string ends with 'x'"],
    ['contains', "contains('x') — string contains 'x'"],
    ['matches', 'matches(regex) — string matches a regex'],
    ['size', 'size() — number of characters'],
])

const IDENT_CHAR_RE = /[A-Za-z0-9_]/

/**
 * Scan backward from `cursor` while the previous character is an identifier
 * character. Returns the start index and the partial identifier text under
 * the cursor (mirrors the Java engine's `identTokenStart(view)` /
 * `identPartial(...)` pair — kept inline here so the TS port doesn't drift
 * away from the Java original on the most repeated cursor-scan idiom).
 */
function identTokenBefore(source: string, cursor: number): {start: number; text: string} {
    let i = cursor
    while (i > 0 && IDENT_CHAR_RE.test(source.charAt(i - 1))) i--
    return {start: i, text: source.substring(i, cursor)}
}

/**
 * Scan forward from `from` while the character is an identifier character.
 * Returns the end index. Used to consume the trailing identifier chars after
 * the cursor for mid-identifier splice (e.g. `pilot.ra|nk` → replace
 * `ra|nk` with `rank`).
 */
function identTokenEnd(source: string, from: number): number {
    let end = from
    while (end < source.length && IDENT_CHAR_RE.test(source.charAt(end))) end++
    return end
}

function iterVarFieldSuggestion(display: string, replacement: CelSuggestion['replacement']): CelSuggestion {
    return {text: display, kind: Kind.FILTER_FIELD, valueType: null, description: null, replacement}
}

/**
 * Append `!` and `(` unary-opener suggestions to a pool — the canonical
 * "lambda body / inline-expression start" tail.
 */
function appendUnaryOpeners(out: CelSuggestion[]): CelSuggestion[] {
    out.push(celSuggestion('!', Kind.LOGICAL, null, SYMBOL_DESCRIPTIONS.get('!') ?? null))
    out.push(celSuggestion('(', Kind.LOGICAL, null, SYMBOL_DESCRIPTIONS.get('(') ?? null))
    return out
}

/**
 * Bare partial identifier under the cursor that is NOT preceded by `.`
 * (root-pool position). Returns null when no partial exists, or when the
 * partial sits inside a path. Mirrors the value/operator-identifier
 * common opening in the Java engine.
 */
function barePartialUnderCursor(source: string, cursor: number): {partialStart: number; partial: string} | null {
    const {start: partialStart, text: partial} = identTokenBefore(source, cursor)
    if (partial.length === 0) return null
    if (partialStart > 0 && source.charAt(partialStart - 1) === '.') return null
    return {partialStart, partial}
}

/**
 * Re-emit each suggestion with a `[tokenStart, cursor)` replacement so the
 * accepted text overwrites the partial token under the cursor instead of
 * inserting at it. Used for partial-prefix narrowing in pool-driven paths.
 */
function spliceMatches(matches: readonly CelSuggestion[], tokenStart: number, cursor: number): CelSuggestion[] {
    return matches.map(s => ({...s, replacement: {start: tokenStart, end: cursor, text: s.text}}))
}

/**
 * Scan backward from `pathEnd` past whitespace, then past identifier/dot
 * characters, returning the dotted path substring (e.g. `pilot.homeworld.name`).
 * Returns an empty string when the scan finds no path characters.
 */
function dottedPathBefore(source: string, pathEnd: number): {dottedPath: string; pathStart: number} {
    let end = pathEnd
    while (end > 0 && /\s/.test(source.charAt(end - 1))) end--
    let start = end
    while (start > 0 && (IDENT_CHAR_RE.test(source.charAt(start - 1)) || source.charAt(start - 1) === '.')) {
        start--
    }
    return {dottedPath: source.substring(start, end), pathStart: start}
}

function operatorSuggestionsForType(type: TypeRef | null, ranked = false): CelSuggestion[] | null {
    const ops = operatorsForType(type, ranked)
    if (ops.length === 0) return null
    return ops.map(op => celSuggestion(op, Kind.OPERATOR, null, SYMBOL_DESCRIPTIONS.get(op) ?? null))
}

/**
 * Natural next-step pool after a boolean-valued expression: `&&`, `||`, plus
 * `)` when an enclosing call paren is still open.
 */
const LIST_MACRO_NAMES = ['exists', 'all', 'filter', 'map', 'exists_one'] as const

/**
 * If `source.substring(0, macroEnd)` ends with `.<list-macro>`, return the
 * macro name and the start index of the macro identifier; otherwise null.
 */
function macroBefore(source: string, macroEnd: number): {macro: string; macroStart: number} | null {
    let macroStart = macroEnd
    while (macroStart > 0 && IDENT_CHAR_RE.test(source.charAt(macroStart - 1))) macroStart--
    const macro = source.substring(macroStart, macroEnd)
    if (!(LIST_MACRO_NAMES as readonly string[]).includes(macro)) return null
    if (macroStart === 0 || source.charAt(macroStart - 1) !== '.') return null
    return {macro, macroStart}
}

/**
 * From an iter-var start position, walk left through whitespace expecting an
 * opening `(` then a list-macro identifier. Returns the macro info or null.
 */
function macroOpenBeforeVar(source: string, varStart: number): {macro: string; macroStart: number} | null {
    let k = varStart
    while (k > 0 && /\s/.test(source.charAt(k - 1))) k--
    if (k === 0 || source.charAt(k - 1) !== '(') return null
    return macroBefore(source, k - 1)
}

/**
 * Walk backward from `from` matching `)` / `(` pairs until depth returns to
 * zero. Returns the position of the matching `(`, or null when no match.
 */
function findMatchingOpenParen(source: string, from: number): number | null {
    let depth = 1
    let j = from
    while (j >= 0 && depth > 0) {
        const c = source.charAt(j)
        if (c === ')') depth++
        else if (c === '(') depth--
        if (depth === 0) return j
        j--
    }
    return null
}

function logicalChainPool(openParenDepth: number): CelSuggestion[] {
    const out: CelSuggestion[] = [
        celSuggestion('&&', Kind.LOGICAL, null, SYMBOL_DESCRIPTIONS.get('&&') ?? null),
        celSuggestion('||', Kind.LOGICAL, null, SYMBOL_DESCRIPTIONS.get('||') ?? null),
    ]
    if (openParenDepth > 0) {
        out.push(celSuggestion(')', Kind.CLOSER, null, SYMBOL_DESCRIPTIONS.get(')') ?? null))
    }
    return out
}

const COMPARISON_OPS = ['==', '!=', '<=', '>=', '<', '>'] as const

/**
 * If the substring `source.substring(0, end).trimEnd()` ends with a comparison
 * operator, return the operator and the index immediately before any trailing
 * whitespace; otherwise return null.
 */
function comparisonOpBefore(source: string, end: number): {op: string; opEnd: number} | null {
    let j = end
    while (j > 0 && /\s/.test(source.charAt(j - 1))) j--
    for (const candidate of COMPARISON_OPS) {
        if (source.substring(0, j).endsWith(candidate)) return {op: candidate, opEnd: j}
    }
    return null
}

/**
 * Whether the cursor sits inside an open `cel.bind(...)` argument list —
 * the nearest unclosed `(` on the scan-back path is opened by `cel.bind`.
 * Used to switch the value-end dispatch from logical chain (`&&`/`||`) to
 * the cel.bind-aware `,` (next arg) / `)` (close call).
 */
function cursorInsideCelBindArgs(source: string, cursor: number): boolean {
    const prefix = source.substring(0, cursor)
    let depth = 0
    for (let i = prefix.length - 1; i >= 0; i--) {
        const c = prefix.charAt(i)
        if (c === ')' || c === ']') {
            depth++
        } else if (c === '[' && depth > 0) {
            depth--
        } else if (c === '(') {
            if (depth === 0) {
                let j = i
                while (j > 0 && /\s/.test(prefix.charAt(j - 1))) j--
                const end = j
                let start = j
                while (start > 0 && /[A-Za-z0-9_.]/.test(prefix.charAt(start - 1))) start--
                return prefix.substring(start, end) === 'cel.bind'
            }
            depth--
        }
    }
    return false
}

export class IamCelSuggestionEngine {
    private readonly compiler: IamCelCompiler
    private readonly schemaField: IamRuleExpressionSchema
    private readonly enums: EnumValueProvider
    private readonly replacements = new CelSuggestionReplacements()

    private readonly attributeEnumTypes: ReadonlyMap<string, string>
    private readonly bindableRowTables: ReadonlyMap<string, string>

    constructor(
        compiler?: IamCelCompiler,
        enums: EnumValueProvider = EMPTY_ENUM_VALUE_PROVIDER,
        attributeEnumTypes: ReadonlyMap<string, string> = new Map(),
        bindableRowTables: ReadonlyMap<string, string> = new Map(),
    ) {
        this.compiler = compiler ?? new IamCelCompiler()
        this.schemaField = this.compiler.schema()
        this.enums = enums
        this.attributeEnumTypes = attributeEnumTypes
        this.bindableRowTables = bindableRowTables
    }

    schema(): IamRuleExpressionSchema {
        return this.schemaField
    }

    enumValueProvider(): EnumValueProvider {
        return this.enums
    }

    suggest(celSource: string, cursorOffset: number): CelSuggestion[] {
        if (cursorOffset < 0 || cursorOffset > celSource.length) return []

        const view = lexTokenView(celSource, cursorOffset)
        // NOTE: Build the AST cursor context once — replaces the regex-based source
        // pattern scans in lambda + cel.bind detection.
        const ctx = buildCursorContext(celSource, cursorOffset)
        let result: CelSuggestion[]

        const tryPaths: Array<() => CelSuggestion[] | null> = [
            () => this.celBindArgument(celSource, cursorOffset, ctx),
            () => this.celBindBoundAfterDot(celSource, cursorOffset, ctx),
            () => this.staticAnswerFromTokens(view, ctx),
            () => this.mapIndexerKey(celSource, cursorOffset, ctx),
            () => this.inOpenStringEnum(celSource, cursorOffset, ctx),
            () => this.iterVarPlaceholder(celSource, cursorOffset),
            () => this.methodOpenParen(celSource, cursorOffset, view),
            () => this.partialValueIdentifier(celSource, cursorOffset, ctx),
            () => this.partialOperatorIdentifier(celSource, cursorOffset, view),
            () => this.partialOperatorCompletion(celSource, cursorOffset, view),
            () => this.afterInOpener(celSource, cursorOffset),
            () => this.inListValues(celSource, cursorOffset, ctx, view),
            () => this.operatorPositionCompletion(celSource, cursorOffset, view),
            () => this.valuePositionCompletion(celSource, cursorOffset, view, ctx),
            () => this.lambdaBodyCompletion(celSource, cursorOffset, ctx),
            () => this.afterDotPathCompletion(celSource, cursorOffset, ctx),
            () => this.afterDotOnFilterCall(celSource, cursorOffset),
            () => this.lambdaBodyBareIdent(celSource, cursorOffset, ctx),
            () => this.logicalConnectorAfterValue(celSource, cursorOffset, view),
            () => this.exactMatchOrPartialPrefix(celSource, cursorOffset, view, ctx),
        ]
        result = []
        for (const fn of tryPaths) {
            const answer = fn()
            if (answer !== null) {
                result = answer
                break
            }
        }
        if (result.length === 0) {
            // PORT TODO: full AST-driven suggestFromAst.
            const injected = celSource.slice(0, cursorOffset) + CURSOR + celSource.slice(cursorOffset)
            try {
                parse(injected)
            } catch {
                return []
            }
        }

        return this.replacements.applyDefaults(result, celSource, cursorOffset)
    }

    /**
     * Cursor after a value-ending token (string literal, number, `)`, `]`)
     * — suggest logical connectors `&&` / `||` plus `)` for an unclosed
     * enclosing paren. Handles both trailing-space and no-trailing-space.
     */
    private logicalConnectorAfterValue(source: string, cursor: number, view: TokenView): CelSuggestion[] | null {
        const last = view.lastNonWs
        if (last === null) return null
        if (this.replacements.inOpenStringLiteral(source, cursor)) return null
        const isValueEnd = last.kind === 'STRING' || last.kind === 'NUM'
            || (last.kind === 'PUNCT' && (last.text === ')' || last.text === ']'))
        if (!isValueEnd) return null
        if (view.openBracketDepth > 0) return null

        // WHY: value-end inside an open cel.bind arg list — the next
        // syntactic step is `,` (next arg) or `)` (close call), NOT a
        // logical chain. Detect by scanning back from cursor for an
        // unclosed `cel.bind(` opener at the nearest enclosing paren.
        if (cursorInsideCelBindArgs(source, cursor)) {
            return [
                celSuggestion(',', Kind.CLOSER, null, 'Next cel.bind argument',
                    {start: cursor, end: cursor, text: ', '}),
            ]
        }

        // NOTE: `]` after a map indexer (`<map>['key']`) yields the indexed VALUE
        // (typically a struct), not a boolean — surface equality operators
        // instead of logical chain. `]` after `in [...]` yields a boolean —
        // surface logical chain.
        if (last.kind === 'PUNCT' && last.text === ']') {
            const isInList = this.bracketIsInListLiteral(source, last.start)
            if (!isInList) {
                const out: CelSuggestion[] = []
                for (const op of ['==', '!=']) {
                    out.push(celSuggestion(op, Kind.OPERATOR, null, SYMBOL_DESCRIPTIONS.get(op) ?? null))
                }
                return out
            }
        }

        return logicalChainPool(view.openParenDepth)
    }

    /**
     * Walk back from a closing `]` to find its matching `[`, then check
     * whether the token immediately before is `in` — that's an `in [...]`
     * list literal. Anything else is a map indexer.
     */
    private bracketIsInListLiteral(source: string, closeBracketPos: number): boolean {
        let depth = 1
        let i = closeBracketPos - 1
        while (i >= 0 && depth > 0) {
            const c = source.charAt(i)
            if (c === ']') depth++
            else if (c === '[') {
                if (depth === 1) break
                depth--
            }
            i--
        }
        if (i < 0) return false
        // i is at the matching `[`. Scan back for `in`.
        let j = i
        while (j > 0 && /\s/.test(source.charAt(j - 1))) j--
        if (j >= 2 && source.substring(j - 2, j) === 'in') {
            if (j < 3 || !IDENT_CHAR_RE.test(source.charAt(j - 3))) return true
        }
        return false
    }

    /**
     * Token-level: cursor right after a partial operator character
     * (`=`, `!`, `<`, `>`) following a value-ending token, and not yet a
     * complete operator. Suggest the full operator. Mirrors
     * IamCelSuggestionEngine.partialOperatorCompletion.
     */
    private partialOperatorCompletion(source: string, cursor: number, view: TokenView): CelSuggestion[] | null {
        if (cursor === 0) return null
        const last = source.charAt(cursor - 1)
        if (last !== '=' && last !== '!' && last !== '<' && last !== '>') return null
        if (cursor >= 2) {
            const tail = source.substring(cursor - 2, cursor)
            if (tail === '==' || tail === '!=' || tail === '<=' || tail === '>=') return null
        }
        if (!view.cursorIsAfterValueWithOptionalOpChar) return null

        let raw: readonly string[]
        switch (last) {
            case '=': raw = ['=='] ; break
            case '!': raw = ['!=']; break
            case '<': raw = ['<', '<=']; break
            case '>': raw = ['>', '>=']; break
            default: raw = []
        }
        const start = cursor - 1
        const out: CelSuggestion[] = []
        for (const op of raw) {
            out.push({
                text: op, kind: Kind.OPERATOR,
                valueType: null,
                description: SYMBOL_DESCRIPTIONS.get(op) ?? null,
                replacement: {start, end: cursor, text: `${op} `},
            })
        }
        return out
    }

    /**
     * Token-level: cursor after `<field> ` (identifier followed by trailing
     * space). Suggest comparison operators applicable to the field's resolved
     * type. Mirrors the OPERATOR-intent branch of SentinelPlan. Also handles
     * the post-method-call case (`<expr>.size() ` etc.) — the resolved type
     * is whatever the method returns (size → number, filter → list, …).
     */
    private operatorPositionCompletion(source: string, cursor: number, view: TokenView): CelSuggestion[] | null {
        if (!view.hasTrailingWhitespace) return null
        if (view.lastNonWs === null) return null

        // Method-call result case: last non-ws is `)`. Match `.<method>(...)`.
        if (view.lastNonWs.kind === 'PUNCT' && view.lastNonWs.text === ')') {
            // Walk back from the `)` to its matching `(`, then read the method name.
            const openParen = findMatchingOpenParen(source, view.lastNonWs.start - 1)
            if (openParen === null) return null
            let macroStart = openParen
            while (macroStart > 0 && IDENT_CHAR_RE.test(source.charAt(macroStart - 1))) macroStart--
            const macro = source.substring(macroStart, openParen)
            if (macroStart === 0 || source.charAt(macroStart - 1) !== '.') return null
            // NOTE: Boolean-returning method calls — natural next step is a logical
            // chain (&&, ||) plus a closer for an open enclosing paren. Other
            // method results emit type-appropriate operators.
            const boolMacros = ['exists', 'all', 'exists_one', 'startsWith', 'endsWith', 'contains', 'matches']
            if (boolMacros.includes(macro)) return logicalChainPool(view.openParenDepth)
            let resultType: TypeRef | null
            if (macro === 'size') resultType = {kind: 'scalar', fieldType: 'NUMBER'}
            else if (macro === 'filter' || macro === 'map') resultType = {kind: 'list', elemType: {kind: 'scalar', fieldType: 'DYN'}}
            else return null
            return operatorSuggestionsForType(resultType)
        }

        if (view.lastNonWs.kind !== 'IDENT') return null
        const {dottedPath} = dottedPathBefore(source, cursor)
        if (dottedPath.length === 0) return null
        const parts = dottedPath.split('.')
        if (!this.schemaField.registry().containsRoot(parts[0])) {
            // WHY: trailing-whitespace inside a lambda body leaves ctx null
            // (partial source doesn't parse cleanly). Fall back to text-based
            // detection so `it.<field>` resolves through the enclosing
            // `<collection>.<macro>(<iterVar>, ` to the right type + ranked
            // flag.
            return this.iterVarOperatorPosition(source, cursor, dottedPath)
        }
        const ranked = this.schemaField.registry().resolveRanked(dottedPath)
        const type = this.schemaField.typeForPath(dottedPath)
        return operatorSuggestionsForType(type, ranked)
    }

    /**
     * Issue 781 — text-based scan back from the operator-position cursor for
     * the enclosing lambda. Required because `buildCursorContext` typically
     * returns null on trailing-whitespace input — the suggester can't rely on
     * the AST comprehension chain here. Mirrors {@link lambdaBodyCompletion}'s
     * text-based scan (which already handles the comma-cursor case).
     */
    private iterVarOperatorPosition(source: string, cursor: number, dottedPath: string): CelSuggestion[] | null {
        const parts = dottedPath.split('.')
        if (parts.length < 2) return null
        const iterVar = parts[0]
        const remainder = parts.slice(1).join('.')
        let pathEnd = cursor
        while (pathEnd > 0 && /\s/.test(source.charAt(pathEnd - 1))) pathEnd--
        let pathStart = pathEnd - dottedPath.length
        if (pathStart < 0 || source.substring(pathStart, pathEnd) !== dottedPath) return null
        // Walk back further to find `, ` then the matching `(`.
        let i = pathStart;
        while (i > 0 && /\s/.test(source.charAt(i - 1))) i--
        if (i === 0 || source.charAt(i - 1) !== ',') return null
        i--
        while (i > 0 && /\s/.test(source.charAt(i - 1))) i--
        // The chars right before the comma must be the iter-var name.
        const varEnd = i
        let varStart = varEnd
        while (varStart > 0 && IDENT_CHAR_RE.test(source.charAt(varStart - 1))) varStart--
        if (varStart === varEnd) return null
        if (source.substring(varStart, varEnd) !== iterVar) return null
        const macroInfo = macroOpenBeforeVar(source, varStart)
        if (macroInfo === null) return null
        let collectionEnd = macroInfo.macroStart - 1;
        let collectionStart = collectionEnd
        while (collectionStart > 0
            && (IDENT_CHAR_RE.test(source.charAt(collectionStart - 1)) || source.charAt(collectionStart - 1) === '.')) {
            collectionStart--
        }
        const collectionPath = source.substring(collectionStart, collectionEnd)
        if (collectionPath.length === 0) return null
        const collectionParts = collectionPath.split('.')
        const collectionName = collectionParts[collectionParts.length - 1]
        const registryPath = `${collectionName}.${remainder}`
        const registry = this.schemaField.registry()
        const ranked = registry.resolveRanked(registryPath)
        // Derive CelType through the schema's collection-element struct.
        const collType = this.schemaField.typeForPath(collectionPath)
        if (collType === null || !isList(collType) || !isStruct(collType.elemType)) return null
        let cur: TypeRef = collType.elemType
        for (const seg of remainder.split('.')) {
            const next = this.schemaField.stepInto(cur, seg)
            if (next === null) return null
            cur = next
        }
        return operatorSuggestionsForType(cur, ranked)
    }

    /**
     * Token-level: cursor after `<field> == ` / `<field> != ` etc. Supports
     * both space-after (`== |`) and no-space-after (`==|`) variants. Suggest
     * null + enum codes when the field is an enum, else null.
     */
    private valuePositionCompletion(source: string, cursor: number, view: TokenView, ctx: CursorContext | null): CelSuggestion[] | null {
        const trimmedRight = source.substring(0, cursor).trimEnd()
        let op = ''
        for (const candidate of COMPARISON_OPS) {
            if (trimmedRight.endsWith(candidate)) {
                op = candidate
                break
            }
        }
        if (op === '') return null
        if (!view.hasTrailingWhitespace && (op === '<' || op === '>')) return null

        const {dottedPath} = dottedPathBefore(source, trimmedRight.length - op.length)
        if (dottedPath.length === 0) return null

        const out = this.nullPlusEnumCandidates(ctx, dottedPath)
        // NOTE: No enum context — for ordering operators (<, <=, >, >=) the RHS
        // is typically a cross-field reference, surface the root pool too.
        // For equality (==, !=) only `null` makes sense.
        if (this.resolveEnumTypeForPath(ctx, dottedPath) === null
                && (op === '<' || op === '<=' || op === '>' || op === '>=')) {
            for (const s of this.expressionStartPool()) out.push(s)
        }
        return out
    }

    /**
     * Token-level: cursor inside a list-macro body at the predicate position
     * `.filter(it, |`, `.exists(g, |`, etc. Suggest iter-var-prefixed element
     * field names. The detection is text-based by scanning backward for the
     * macro-opening pattern.
     */
    private lambdaBodyCompletion(source: string, cursor: number, ctx: CursorContext | null): CelSuggestion[] | null {
        // NOTE: Match `<path>.<macro>(<iterVar>, ` with `<path>` ending at the cursor's left.
        // Scan from cursor back for the comma+space, then iterVar, then ( then macro name.
        let i = cursor
        // Skip trailing whitespace (the catalog cursors usually sit immediately after `, `).
        while (i > 0 && /\s/.test(source.charAt(i - 1))) i--
        if (i === 0 || source.charAt(i - 1) !== ',') return null
        let j = i - 1 // at the comma
        // iter-var identifier
        let varEnd = j
        while (varEnd > 0 && /\s/.test(source.charAt(varEnd - 1))) varEnd--
        let varStart = varEnd
        while (varStart > 0 && IDENT_CHAR_RE.test(source.charAt(varStart - 1))) varStart--
        if (varStart === varEnd) return null
        const iterVar = source.substring(varStart, varEnd)
        const macroInfo = macroOpenBeforeVar(source, varStart)
        if (macroInfo === null) return null
        const {macroStart} = macroInfo
        // Receiver path (collection)
        let pathEnd = macroStart - 1
        let pathStart = pathEnd
        while (pathStart > 0 && (IDENT_CHAR_RE.test(source.charAt(pathStart - 1)) || source.charAt(pathStart - 1) === '.')) {
            pathStart--
        }
        const collectionPath = source.substring(pathStart, pathEnd)
        if (collectionPath.length === 0) return null
        const elemInfo = this.resolveCollectionElement(ctx, collectionPath)
        if (elemInfo === null) return null

        const tokenStart = cursor
        const out: CelSuggestion[] = []
        for (const f of elemInfo.elementStruct.fieldNames) {
            const display = `${iterVar}.${f}`
            out.push(iterVarFieldSuggestion(display, {start: tokenStart, end: cursor, text: display}))
        }
        return appendUnaryOpeners(out)
    }

    /**
     * After-dot path completion — token-level shortcut for the very common
     * case where the cursor sits at the end of a dotted path (`identity.|` /
     * `party.|` / `identity.functionGroups.|`) or mid-identifier inside one
     * (`identity.li|`). Detects the path, looks up the type via the schema,
     * and emits field/method completions.
     *
     * Also handles paths whose first segment is an iter-var from an
     * enclosing lambda — resolves the iter-var against the collection's
     * element struct.
     */
    private afterDotPathCompletion(source: string, cursor: number, ctx: CursorContext | null): CelSuggestion[] | null {
        const {start: segmentStart, text: partial} = identTokenBefore(source, cursor)
        const segmentEnd = identTokenEnd(source, cursor)
        if (segmentStart === 0 || source.charAt(segmentStart - 1) !== '.') return null
        const dotPos = segmentStart - 1
        let pathStart = dotPos
        while (pathStart > 0 && (IDENT_CHAR_RE.test(source.charAt(pathStart - 1)) || source.charAt(pathStart - 1) === '.')) {
            pathStart--
        }
        const dottedPath = source.substring(pathStart, dotPos)
        if (dottedPath.length === 0) return null

        let type: TypeRef | null = null
        let operandPath: string = dottedPath
        let providerLookupPath: string = dottedPath
        const parts = dottedPath.split('.')
        if (this.schemaField.registry().containsRoot(parts[0])) {
            type = this.schemaField.typeForPath(dottedPath)
        } else {
            const lambdaInfo = this.resolveIterVarPath(ctx, dottedPath)
            if (lambdaInfo === null) return null
            type = lambdaInfo.type
            operandPath = lambdaInfo.operandPath
            // NOTE: For provider lookup (attribute keys), use the element-container
            // name + remaining segments. `it.attributes` with iter-var bound
            // to `pilot.ships` elements (Ship) → provider key `ship.attributes`.
            if (lambdaInfo.elementContainer !== null) {
                const tailSegments = parts.slice(1)
                providerLookupPath = `${lambdaInfo.elementContainer}${tailSegments.length > 0 ? '.' + tailSegments.join('.') : ''}`
            }
        }

        const all = this.fieldsFromType(providerLookupPath, type)
        if (all.length === 0) return null
        // NOTE: Exact match against a field name → drop the echo + emit next-step
        // ONLY when the cursor sits at the END of the typed identifier. When
        // the cursor is MID-identifier (user is still typing), keep the
        // suggestion so the bidirectional splice completes it WYSIWYG.
        const fullToken = source.substring(segmentStart, segmentEnd)
        const cursorAtEnd = cursor === segmentEnd
        if (fullToken.length > 0 && cursorAtEnd) {
            const exact = all.find(s => s.text === fullToken)
            if (exact !== undefined) {
                return this.exactMatchNextStep(exact, all, operandPath, fullToken, type, source, segmentEnd, segmentStart)
            }
        }
        return spliceMatches(partial.length === 0 ? all : all.filter(s => s.text.startsWith(partial)), segmentStart, segmentEnd)
    }

    /**
     * Given a dotted-path whose root segment is NOT a schema root, check if
     * it's an iter-var bound by an enclosing lambda. Uses the AST context's
     * comprehensionChain — innermost first — to find the matching iterVar
     * and resolve its iterRange. Supports nested lambdas because the chain
     * itself contains all ancestors.
     */
    private resolveIterVarPath(ctx: CursorContext | null, dottedPath: string): {type: TypeRef | null; operandPath: string; elementContainer: string | null} | null {
        if (ctx === null) return null
        const parts = dottedPath.split('.')
        const iterVar = parts[0]
        const remainder = parts.slice(1)
        for (const comp of ctx.comprehensionChain) {
            if (comp.exprKind.case !== 'comprehensionExpr') continue
            if (comp.exprKind.value.iterVar !== iterVar) continue
            const iterRange = comp.exprKind.value.iterRange
            const receiverPath = dottedPathOf(iterRange)
            if (receiverPath === null) continue
            const elemInfo = this.resolveCollectionElement(ctx, receiverPath)
            if (elemInfo === null) continue
            let cur: TypeRef = {kind: 'struct', struct: elemInfo.elementStruct}
            let containerName: string | null = elemInfo.elementContainer
            let ok = true
            for (const seg of remainder) {
                const next = this.schemaField.stepInto(cur, seg)
                if (next === null) { ok = false; break }
                cur = next
                if (isStruct(next) || isList(next)) containerName = seg
            }
            if (!ok) continue
            return {type: cur, operandPath: `${receiverPath}[].${remainder.join('.')}`, elementContainer: containerName}
        }
        return null
    }

    /**
     * Resolve a dotted receiver path to its collection-element struct +
     * container name. The receiver may be a plain schema path
     * (`pilot.ships`), or itself rooted at an outer iter-var
     * (`it.incomingRelationships`) — outer resolution recurses through the
     * AST comprehension chain.
     */
    private resolveCollectionElement(ctx: CursorContext | null, receiverPath: string): {elementStruct: StructType; elementContainer: string} | null {
        const parts = receiverPath.split('.')
        if (this.schemaField.registry().containsRoot(parts[0])) {
            const t = this.schemaField.typeForPath(receiverPath)
            if (t === null || !isList(t)) return null
            if (!isStruct(t.elemType)) return null
            return {elementStruct: t.elemType.struct, elementContainer: parts[parts.length - 1]}
        }
        const outer = this.resolveIterVarPath(ctx, receiverPath)
        if (outer === null || outer.type === null) return null
        if (!isList(outer.type)) return null
        if (!isStruct(outer.type.elemType)) return null
        const container = outer.elementContainer ?? parts[parts.length - 1]
        return {elementStruct: outer.type.elemType.struct, elementContainer: container}
    }

    /**
     * Cursor on a bare partial identifier in value position (after a complete
     * comparison operator) — match against `null` and any enum codes for the
     * LHS field. E.g. `pilot.rank != n|` → `null`, `pilot.rank == nu|` → `null`.
     */
    private partialValueIdentifier(source: string, cursor: number, ctx: CursorContext | null): CelSuggestion[] | null {
        const bare = barePartialUnderCursor(source, cursor)
        if (bare === null) return null
        const {partialStart, partial} = bare

        const opInfo = comparisonOpBefore(source, partialStart)
        if (opInfo === null) return null
        const {dottedPath} = dottedPathBefore(source, opInfo.opEnd - opInfo.op.length)
        if (dottedPath.length === 0) return null

        const candidates: string[] = ['null']
        const enumTypeName = this.resolveEnumTypeForPath(ctx, dottedPath)
        if (enumTypeName !== null) {
            for (const code of this.enums.activeCodes(enumTypeName)) candidates.push(code)
        }
        const out: CelSuggestion[] = []
        for (const c of candidates) {
            if (!c.startsWith(partial)) continue
            const kind = c === 'null' ? Kind.VALUE : Kind.VALUE
            const valueType = c === 'null' ? 'null' : 'enum'
            const splicedText = c === 'null' ? 'null' : `'${c}'`
            out.push({
                text: c,
                kind,
                valueType,
                description: null,
                replacement: {start: partialStart, end: cursor, text: splicedText},
            })
        }
        if (out.length === 0) return null
        return out
    }

    /**
     * Cursor on a bare partial identifier in OPERATOR position (after a
     * value-ending token + whitespace + partial). Match against `in`.
     * E.g. `pilot.midiChlorians i|` → `in`.
     */
    private partialOperatorIdentifier(source: string, cursor: number, view: TokenView): CelSuggestion[] | null {
        const bare = barePartialUnderCursor(source, cursor)
        if (bare === null) return null
        const {partialStart, partial} = bare
        // Need whitespace immediately before the partial.
        let j = partialStart
        while (j > 0 && /\s/.test(source.charAt(j - 1))) j--
        if (j === partialStart) return null
        // NOTE: Preceding non-ws token must be a value-ending kind. Quick check via
        // a backward char inspection.
        const charBefore = source.charAt(j - 1)
        const isValueEnd = IDENT_CHAR_RE.test(charBefore) || charBefore === ')' || charBefore === ']'
            || charBefore === "'" || charBefore === '"'
        if (!isValueEnd) return null
        // NOTE: When partial is already `in`, fall through to afterInOpener which
        // suggests `[` (the next step after completing the `in` keyword).
        if (partial === 'in') return null
        if (!'in'.startsWith(partial)) return null
        void view
        return [{
            text: 'in',
            kind: Kind.OPERATOR,
            valueType: null,
            description: SYMBOL_DESCRIPTIONS.get('in') ?? null,
            replacement: {start: partialStart, end: cursor, text: 'in '},
        }]
    }

    /**
     * cel.bind(name, source, body) — second argument is a row-table-typed
     * source expression. When the cursor sits in arg-2 position with no
     * complete value typed, surface quoted row-table names. Also handles
     * partial typed root (`cel.bind(p, pilot|`).
     */
    private celBindArgument(source: string, cursor: number, ctx: CursorContext | null): CelSuggestion[] | null {
        if (this.bindableRowTables.size === 0) return null
        if (ctx === null) return null
        if (ctx.celBindChain.length === 0) return null
        const frame = ctx.celBindChain[0]
        if (frame.argIndex !== 1 && frame.argIndex !== 2) return null
        // NOTE: Only fire when the cursor is the DIRECT argument as a bare
        // identifier (not a Select / sub-expression at that slot).
        if (ctx.parent !== frame.call) return null
        if (ctx.cursorNode.exprKind.case !== 'identExpr') return null

        const {start: partialStart, text: partial} = identTokenBefore(source, cursor)
        if (partial.length > 0) {
            // NOTE: User has started typing — emit bare matches from the schema
            // root pool (no quotes; matches the WYSIWYG splice).
            const out: CelSuggestion[] = []
            for (const root of this.schemaField.rootNames()) {
                if (!root.startsWith(partial)) continue
                out.push({
                    text: root,
                    kind: Kind.ROOT_KEYWORD,
                    valueType: null,
                    description: ROOT_DESCRIPTIONS.get(root) ?? null,
                    replacement: {start: partialStart, end: cursor, text: root},
                })
            }
            if (out.length === 0) return null
            return out
        }
        // NOTE: Empty position — emit the expression-start pool with kinds
        // classified by CelSuggestionReplacements. valuePos=true so plain
        // identifiers get Kind.VALUE and applyDefaults will quote them;
        // `size` (LIST_MACRO), `cel.bind` (STATIC_FIELD via the `.`), `!`/`(`
        // (LOGICAL) keep their natural kinds and stay unquoted.
        const pool: string[] = [
            ...this.bindableRowTables.keys(),
            ...CEL_STDLIB,
            ...UNARY_OPENERS,
        ]
        const out: CelSuggestion[] = []
        for (const text of pool) {
            const kind = classifyKind(text, true, false)
            const desc = ROOT_DESCRIPTIONS.get(text)
                ?? STDLIB_DESCRIPTIONS.get(text)
                ?? SYMBOL_DESCRIPTIONS.get(text)
                ?? null
            out.push(celSuggestion(text, kind, null, desc))
        }
        return out
    }

    /**
     * cel.bind body: `cel.bind(name, source, ... name.| ...)` — when the
     * cursor sits at `<name>.<partial>|` and `<name>` is bound to a
     * row-table-typed source, return the fields of the source's element.
     */
    private celBindBoundAfterDot(source: string, cursor: number, ctx: CursorContext | null): CelSuggestion[] | null {
        if (this.bindableRowTables.size === 0 || ctx === null) return null
        const {start: segmentStart, text: partial} = identTokenBefore(source, cursor)
        if (segmentStart === 0 || source.charAt(segmentStart - 1) !== '.') return null
        const segmentEnd = identTokenEnd(source, cursor)
        const dotPos = segmentStart - 1
        let pathStart = dotPos
        while (pathStart > 0 && IDENT_CHAR_RE.test(source.charAt(pathStart - 1))) pathStart--
        const boundName = source.substring(pathStart, dotPos)
        if (boundName.length === 0) return null

        // NOTE: Walk the enclosing cel.bind chain to find one whose binding name
        // matches `boundName` AND has the cursor in its arg-2 (body) subtree.
        const elementRoot = this.celBindElementRootFromContext(ctx, boundName)
        if (elementRoot === null) return null
        const elementStruct = this.schemaField.rootType(elementRoot)
        if (elementStruct === null) return null

        const all = this.fieldsFromType(elementRoot, {kind: 'struct', struct: elementStruct})
        const result = spliceMatches(partial.length === 0 ? all : all.filter(s => s.text.startsWith(partial)), segmentStart, segmentEnd)
        return result.length === 0 ? null : result
    }

    private celBindElementRootFromContext(ctx: CursorContext, boundName: string): string | null {
        for (const frame of ctx.celBindChain) {
            if (frame.bindingName !== boundName) continue
            if (frame.source === null) continue
            // NOTE: Source is either a quoted-string constExpr (`'pilot'`) or a
            // call `<rowsVar>[<key>]` (indexed). Both resolve to a schema root.
            const src = frame.source
            if (src.exprKind.case === 'constExpr'
                    && src.exprKind.value.constantKind.case === 'stringValue') {
                const tableName = src.exprKind.value.constantKind.value as string
                const root = this.bindableRowTables.get(tableName)
                if (root !== undefined && this.schemaField.rootType(root) !== null) return root
            }
            // `<rowsVar>['UUID']` parses as a Call `_[_]` or as an index op.
            if (src.exprKind.case === 'callExpr') {
                const call = src.exprKind.value
                if (call.function === '_[_]' && call.args.length === 2) {
                    const receiver = identName(call.args[0])
                    if (receiver !== null && this.schemaField.rootType(receiver) !== null) return receiver
                }
            }
            if (src.exprKind.case === 'identExpr') {
                const name = src.exprKind.value.name
                if (this.schemaField.rootType(name) !== null) return name
            }
        }
        return null
    }

    /**
     * Cursor inside an open string literal that is the KEY of a map indexer
     * (`<map>['<key>|']`) — emit the map's known key set. For
     * `*ByType` fields we pull `relationship_types`; for `<root>.attributes`
     * we go through the FieldNameProvider. Also handles the unquoted partial
     * `<map>[KEY|` shape (CEL accepts both bare and quoted keys here).
     */
    private mapIndexerKey(source: string, cursor: number, ctx: CursorContext | null): CelSuggestion[] | null {
        // Find an open `[` to the left without a matching `]`.
        let depth = 0
        let openBracket = -1
        for (let i = cursor - 1; i >= 0; i--) {
            const c = source.charAt(i)
            if (c === ']') depth++
            else if (c === '[') {
                if (depth === 0) {
                    openBracket = i
                    break
                }
                depth--
            }
        }
        if (openBracket < 0) return null
        // Path before `[`
        let pathEnd = openBracket
        let pathStart = pathEnd
        while (pathStart > 0 && (IDENT_CHAR_RE.test(source.charAt(pathStart - 1)) || source.charAt(pathStart - 1) === '.')) {
            pathStart--
        }
        const receiverPath = source.substring(pathStart, pathEnd)
        if (receiverPath.length === 0) return null
        // Determine if cursor is inside an open quote vs bare.
        const inside = source.substring(openBracket + 1, cursor)
        const quotedMatch = /^\s*(['"])([^'"]*)$/.exec(inside)
        const insideQuote = quotedMatch !== null
        const partial = insideQuote ? quotedMatch[2] : inside.trim()

        // Resolve the map key set.
        const keys = this.resolveMapKeys(ctx, receiverPath)
        if (keys === null) return null

        const out: CelSuggestion[] = []
        const partialStart = cursor - partial.length
        for (const key of keys) {
            // NOTE: Filter by partial ONLY when inside an open quote — outside the
            // quotes the catalog convention is to show all keys regardless of
            // what's typed (the cursor position is positional, not a filter).
            if (insideQuote && !key.startsWith(partial)) continue
            // NOTE: Conventions:
            //   - inside open quote → bare display + bare splice
            //   - bare partial outside quotes (`[CO`) → bare display + bare splice
            //   - empty outside quotes (`[\|`) → quoted display + quoted splice
            const isEmptyOutsideQuote = !insideQuote && partial.length === 0
            const displayText = isEmptyOutsideQuote ? `'${key}'` : key
            const spliceText = isEmptyOutsideQuote ? `'${key}'` : key
            out.push({
                text: displayText,
                kind: Kind.VALUE,
                valueType: 'map-key',
                description: null,
                replacement: {start: partialStart, end: cursor, text: spliceText},
            })
        }
        if (out.length === 0) return null
        return out
    }

    private resolveMapKeys(ctx: CursorContext | null, receiverPath: string): readonly string[] | null {
        const segments = receiverPath.split('.')
        // *ByType: keys are relationship_types
        const last = segments[segments.length - 1]
        if (last === 'incomingRelationshipsByType' || last === 'outgoingRelationshipsByType') {
            return this.enums.activeCodes('relationship_types')
        }
        // <X>.attributes: keys via FieldNameProvider
        if (last === 'attributes') {
            let providerPath = receiverPath
            if (!this.schemaField.registry().containsRoot(segments[0])) {
                const lambdaInfo = this.resolveIterVarPath(ctx, receiverPath)
                if (lambdaInfo !== null) providerPath = lambdaInfo.operandPath.replace('[]', '')
            }
            const provider = this.schemaField.registry().fieldNameProvider(providerPath)
            if (provider !== null) {
                return provider.getFieldNames('').map(s => s.name)
            }
        }
        return null
    }

    /**
     * Cursor inside an open string literal whose LHS is an enum-typed field.
     * Splice the partial against the enum codes (display = enum code, kept
     * inside the open quote).
     */
    private inOpenStringEnum(source: string, cursor: number, ctx: CursorContext | null): CelSuggestion[] | null {
        if (!this.replacements.inOpenStringLiteral(source, cursor)) return null
        // Find the open quote position and the partial inside it.
        let quotePos = cursor - 1
        while (quotePos >= 0 && source.charAt(quotePos) !== "'" && source.charAt(quotePos) !== '"') quotePos--
        if (quotePos < 0) return null
        const partial = source.substring(quotePos + 1, cursor)
        const opInfo = comparisonOpBefore(source, quotePos)
        if (opInfo === null || (opInfo.op !== '==' && opInfo.op !== '!=')) return null
        const {dottedPath} = dottedPathBefore(source, opInfo.opEnd - opInfo.op.length)
        if (dottedPath.length === 0) return null
        const enumTypeName = this.resolveEnumTypeForPath(ctx, dottedPath)
        if (enumTypeName === null) return null

        // NOTE: Emit each enum code as a Kind.VALUE suggestion. Bidirectional
        // splice: replace from start-of-partial THROUGH any matching tail
        // chars (mid-identifier case `'KNI|GHT'`).
        const out: CelSuggestion[] = []
        const openQuote = source.charAt(quotePos)
        const tailStart = cursor
        let tailEnd = tailStart
        while (tailEnd < source.length && IDENT_CHAR_RE.test(source.charAt(tailEnd))) tailEnd++
        const tail = source.substring(tailStart, tailEnd)
        for (const code of this.enums.activeCodes(enumTypeName)) {
            if (!code.startsWith(partial)) continue
            // Trailing-end: if the code ends with the tail, consume it.
            const end = code.endsWith(tail) ? tailEnd : cursor
            // WHY: close the string literal unless a matching quote already
            // follows the consumed range — accepting `'A` must yield `'ACTIVE'`,
            // not the unterminated `'ACTIVE` (issue 998).
            const closed = end < source.length && source.charAt(end) === openQuote
            out.push({
                text: code,
                kind: Kind.VALUE,
                valueType: 'enum',
                description: null,
                replacement: {start: quotePos + 1, end, text: closed ? code : code + openQuote},
            })
        }
        if (out.length === 0) return null
        return out
    }

    /**
     * Cursor right after `<macro>(` — suggest the iter-var placeholder `it`.
     */
    private iterVarPlaceholder(source: string, cursor: number): CelSuggestion[] | null {
        if (cursor === 0 || source.charAt(cursor - 1) !== '(') return null
        if (macroBefore(source, cursor - 1) === null) return null
        return [{
            text: 'it',
            kind: Kind.FILTER_FIELD,
            valueType: null,
            description: 'Iterator variable — reference each list element as `it` in the predicate',
            replacement: {start: cursor, end: cursor, text: 'it, '},
        }]
    }

    /**
     * Cursor right after the `in` keyword — suggest `[` to open the list
     * literal. Handles both `in |` and `in|`.
     */
    private afterInOpener(source: string, cursor: number): CelSuggestion[] | null {
        let i = cursor
        while (i > 0 && /\s/.test(source.charAt(i - 1))) i--
        if (i < 2) return null
        if (source.substring(i - 2, i) !== 'in') return null
        // Must be preceded by whitespace (i.e. it's the `in` keyword, not an identifier suffix)
        if (i >= 3 && IDENT_CHAR_RE.test(source.charAt(i - 3))) return null
        const hasSpaceAfter = i < cursor
        const insertText = hasSpaceAfter ? '[' : ' ['
        return [{
            text: '[',
            kind: Kind.CLOSER,
            valueType: null,
            description: 'Open list literal — values go inside `[...]`',
            replacement: {start: cursor, end: cursor, text: insertText},
        }]
    }

    /**
     * Cursor inside an open `[ ` after `in` — suggest enum values for the LHS,
     * OR `,` / `]` when the cursor sits right after a closed list element
     * (the user just typed `'LOW'` and the next syntactic step is to add
     * another value or close the list, not echo another enum).
     */
    private inListValues(source: string, cursor: number, ctx: CursorContext | null, view: TokenView): CelSuggestion[] | null {
        // NOTE: Detect: cursor inside `[...` of an `in [` — i.e. an OPEN bracket.
        // Scan back tracking depth so a closed `[...]` doesn't trigger.
        let depth = 0
        let bracketPos = -1
        for (let p = cursor - 1; p >= 0; p--) {
            const c = source.charAt(p)
            if (c === ']') depth++
            else if (c === '[') {
                if (depth === 0) {
                    bracketPos = p + 1
                    break
                }
                depth--
            }
        }
        if (bracketPos < 0) return null
        // Now look back from bracketPos for ' in '
        let j = bracketPos - 1
        while (j > 0 && /\s/.test(source.charAt(j - 1))) j--
        if (j < 2 || source.substring(j - 2, j) !== 'in') return null
        if (j >= 3 && IDENT_CHAR_RE.test(source.charAt(j - 3))) return null

        // WHY: cursor right after a closed list element (`in ['LOW'|` or
        // `in ['LOW', 'HIGH'|`) — the user has committed to a value; the
        // next step is `,` (add another) or `]` (close). Echoing more enum
        // values would yield `'LOW''HIGH'`, invalid CEL. Detect by checking
        // the last non-whitespace token: STRING or NUM (closed literal) →
        // suggest separator + closer. Skip when the cursor is inside an
        // OPEN string literal (`in ['` or `in ['PA`) — that case wants
        // enum-value completions.
        const last = view.lastNonWs
        if (last !== null
                && (last.kind === 'STRING' || last.kind === 'NUM')
                && !this.replacements.inOpenStringLiteral(source, cursor)) {
            return [
                celSuggestion(',', Kind.CLOSER, null, 'Add another element to the list',
                    {start: cursor, end: cursor, text: ', '}),
                celSuggestion(']', Kind.CLOSER, null, 'Close the list literal',
                    {start: cursor, end: cursor, text: ']'}),
            ]
        }

        // Field path before `in`
        const {dottedPath} = dottedPathBefore(source, j - 2)
        if (dottedPath.length === 0) return null

        return this.nullPlusEnumCandidates(ctx, dottedPath)
    }

    /**
     * Bare-value pool for an enum-typed comparison RHS: `null` plus every
     * active enum code. Falls back to `[null]` when the path has no enum
     * type registered. Mirrors the Java engine's same-shape helper.
     */
    private nullPlusEnumCandidates(ctx: CursorContext | null, dottedPath: string): CelSuggestion[] {
        const out: CelSuggestion[] = [celSuggestion('null', Kind.VALUE, 'null')]
        const enumTypeName = this.resolveEnumTypeForPath(ctx, dottedPath)
        if (enumTypeName !== null) {
            for (const code of this.enums.activeCodes(enumTypeName)) {
                out.push(celSuggestion(code, Kind.VALUE, 'enum'))
            }
        }
        return out
    }

    /**
     * Cursor at the end of `<path>.filter(<predicate>).` (or any
     * list-macro call) — suggest list methods. The receiver was a call,
     * not a plain path, so afterDotPathCompletion doesn't handle it.
     */
    private afterDotOnFilterCall(source: string, cursor: number): CelSuggestion[] | null {
        // Detect `).` immediately at the cursor.
        if (cursor < 2) return null
        if (source.charAt(cursor - 1) !== '.') return null
        if (source.charAt(cursor - 2) !== ')') return null
        // Walk back from the `)` to its matching `(`, then read the macro name.
        const openParen = findMatchingOpenParen(source, cursor - 3)
        if (openParen === null) return null
        let macroStart = openParen
        while (macroStart > 0 && IDENT_CHAR_RE.test(source.charAt(macroStart - 1))) macroStart--
        const macro = source.substring(macroStart, openParen)
        if (!['filter', 'map', 'exists', 'all', 'exists_one', 'size'].includes(macro)) return null
        // After-dot offers list methods regardless of macro (filter/map → list).
        const out: CelSuggestion[] = []
        for (const m of CEL_LIST_METHODS) {
            out.push({
                text: m, kind: Kind.LIST_MACRO,
                valueType: null,
                description: LIST_MACRO_DESCRIPTIONS.get(m) ?? null,
                replacement: {start: cursor, end: cursor, text: m},
            })
        }
        return out
    }

    /**
     * Cursor inside a lambda body on a bare identifier or partial identifier
     * — suggest iter-var-prefixed field names (`it.<field>`).
     */
    private lambdaBodyBareIdent(source: string, cursor: number, ctx: CursorContext | null): CelSuggestion[] | null {
        if (ctx === null) return null
        const {start: tokenStart, text: partial} = identTokenBefore(source, cursor)
        if (partial.length === 0) return null
        if (tokenStart > 0 && source.charAt(tokenStart - 1) === '.') return null

        for (const frame of this.lambdaIterFrames(ctx)) {
            const out: CelSuggestion[] = []
            for (const f of frame.fieldNames) {
                if (!f.startsWith(partial)) continue
                const display = `${frame.iterVar}.${f}`
                out.push(iterVarFieldSuggestion(display, {start: tokenStart, end: cursor, text: display}))
            }
            if (out.length > 0) return out
        }
        return null
    }

    /**
     * Iterate the enclosing lambda comprehensions (innermost first), skip
     * `cel.bind` frames, and yield one resolved frame per valid one — the
     * iter-var plus the element struct's field names. Lets callers decide
     * whether to filter by partial / require non-empty / fall through.
     */
    private *lambdaIterFrames(ctx: CursorContext): Generator<{iterVar: string; fieldNames: Iterable<string>}> {
        for (const comp of ctx.comprehensionChain) {
            if (comp.exprKind.case !== 'comprehensionExpr') continue
            const c = comp.exprKind.value
            if (c.iterVar === '#unused') continue
            const receiverPath = dottedPathOf(c.iterRange)
            if (receiverPath === null) continue
            const elemInfo = this.resolveCollectionElement(ctx, receiverPath)
            if (elemInfo === null) continue
            yield {iterVar: c.iterVar, fieldNames: elemInfo.elementStruct.fieldNames}
        }
    }

    private resolveEnumTypeForPath(ctx: CursorContext | null, dottedPath: string): string | null {
        const fromAttrMap = this.attributeEnumTypes.get(dottedPath)
        if (fromAttrMap !== undefined) return fromAttrMap

        const parts = dottedPath.split('.')
        if (this.schemaField.registry().containsRoot(parts[0])) {
            return this.schemaField.registry().resolveEnumTypeName(dottedPath)
        }
        // Iter-var prefix — resolve through the AST comprehension chain.
        if (parts.length < 2 || ctx === null) return null
        const iterVar = parts[0]
        const fieldName = parts[1]
        for (const comp of ctx.comprehensionChain) {
            if (comp.exprKind.case !== 'comprehensionExpr') continue
            if (comp.exprKind.value.iterVar !== iterVar) continue
            const receiverPath = dottedPathOf(comp.exprKind.value.iterRange)
            if (receiverPath === null) continue
            const collType = this.schemaField.typeForPath(receiverPath)
            if (collType === null || !isList(collType)) continue
            const elem = collType.elemType
            if (!isStruct(elem)) continue
            const def = elem.struct.fieldDefs.get(fieldName)
            if (def !== undefined) return def.enumTypeName
        }
        return null
    }

    /**
     * Cursor after `<method-name> ` (e.g. `assignedProfiles.exists ` with
     * trailing space) — suggest `(` to open the call.
     */
    private methodOpenParen(source: string, cursor: number, view: TokenView): CelSuggestion[] | null {
        if (!view.hasTrailingWhitespace) return null
        if (view.lastNonWs === null || view.lastNonWs.kind !== 'IDENT') return null
        const ident = view.lastNonWs.text
        const isMethod = ['size', 'exists', 'all', 'filter', 'map', 'exists_one', 'startsWith', 'endsWith', 'contains', 'matches'].includes(ident)
        if (!isMethod) return null
        // Must be preceded by a `.`
        const identStart = view.lastNonWs.start
        if (identStart === 0 || source.charAt(identStart - 1) !== '.') return null
        return [{
            text: '(',
            kind: Kind.CLOSER,
            valueType: null,
            description: 'Open call parenthesis',
            replacement: {start: cursor, end: cursor, text: '('},
        }]
    }

    /**
     * Mirror of {@class IamCelSuggestionEngine}.fieldsFromType — given a
     * dotted-path prefix and the resolved type, emit field/method
     * suggestions. The dynamic-attribute / relationship-alias folding lives
     * inside IamRuleExpressionSchema.structFromFields, so iterating the
     * struct's fieldNames is enough.
     */
    private fieldsFromType(pathPrefix: string, type: TypeRef | null): CelSuggestion[] {
        if (type === null) return []
        if (isStruct(type)) {
            const provider = this.schemaField.registry().fieldNameProvider(`${pathPrefix}.attributes`)
            const providerAttrs = new Map<string, {name: string; displayName: string}>()
            if (provider !== null) {
                for (const s of provider.getFieldNames('')) providerAttrs.set(s.name, s)
            }
            const out: CelSuggestion[] = []
            for (const field of type.struct.fieldNames) {
                const def: FieldDefinition | null = type.struct.fieldDefs.get(field) ?? null
                let kind: typeof Kind[keyof typeof Kind]
                let description: string | null
                if (def !== null) {
                    kind = Kind.STATIC_FIELD
                    description = def.description
                } else if (this.schemaField.isAlias(field)) {
                    kind = Kind.RELATIONSHIP_ALIAS
                    description = `Relationship alias on ${pathPrefix}`
                } else {
                    kind = Kind.ATTRIBUTE
                    const ps = providerAttrs.get(field)
                    description = ps ? ps.displayName : null
                }
                out.push(celSuggestion(field, kind, null, description))
            }
            for (const ps of providerAttrs.values()) {
                if (!type.struct.fieldNames.has(ps.name)) {
                    out.push(celSuggestion(ps.name, Kind.ATTRIBUTE, null, ps.displayName))
                }
            }
            return out
        }
        if (isMap(type)) {
            const provider = this.schemaField.registry().fieldNameProvider(pathPrefix)
            if (provider === null) return []
            const out: CelSuggestion[] = []
            for (const s of provider.getFieldNames('')) {
                out.push(celSuggestion(s.name, Kind.ATTRIBUTE, null, s.displayName))
            }
            return out
        }
        if (isList(type)) {
            const out: CelSuggestion[] = []
            for (const m of CEL_LIST_METHODS) {
                out.push(celSuggestion(m, Kind.LIST_MACRO, null, LIST_MACRO_DESCRIPTIONS.get(m) ?? null))
            }
            return out
        }
        if (isScalar(type) && type.fieldType === 'STRING') {
            const out: CelSuggestion[] = []
            for (const m of CEL_STRING_METHODS) {
                out.push(celSuggestion(m, Kind.STRING_METHOD, null, STRING_METHOD_DESCRIPTIONS.get(m) ?? null))
            }
            return out
        }
        return []
    }

    /**
     * Token-only fast paths that never trigger a CEL parse. Empty input
     * returns the expression-start pool; partial / exact root-keyword
     * matches narrow it; cursor right after a `&&` / `||` also returns the
     * pool (a fresh expression starts there).
     */
    private staticAnswerFromTokens(view: TokenView, ctx: CursorContext | null): CelSuggestion[] | null {
        if (view.lastNonWs === null) {
            return this.expressionStartPool()
        }
        // NOTE: Cursor after `&&` / `||` / `!` / non-lambda `(` : start a fresh
        // expression. The pool is the iter-var-qualified field pool when
        // we're inside a lambda body, else the root pool.
        const prefixTrimmed = view.prefix.trimEnd()
        const newExpressionAfter = (): boolean => {
            if (prefixTrimmed.endsWith('&&') || prefixTrimmed.endsWith('||')) return true
            if (prefixTrimmed.endsWith('!')) {
                const beforeBang = prefixTrimmed.slice(0, -1).trimEnd()
                const isPartialNeq = beforeBang.length > 0 && /[A-Za-z0-9_)\]'"]$/.test(beforeBang)
                return !isPartialNeq
            }
            if (prefixTrimmed.endsWith('(')) {
                let macroEnd = prefixTrimmed.length - 1
                let macroStart = macroEnd
                while (macroStart > 0 && IDENT_CHAR_RE.test(prefixTrimmed.charAt(macroStart - 1))) macroStart--
                const macro = prefixTrimmed.substring(macroStart, macroEnd)
                return !['exists', 'all', 'filter', 'map', 'exists_one'].includes(macro)
            }
            return false
        }
        if (!newExpressionAfter()) return null

        const lambdaPool = this.lambdaExpressionStartPool(ctx, view.cursor)
        if (lambdaPool !== null) return lambdaPool
        return this.expressionStartPool()
    }

    /**
     * Inside a lambda body, the "expression start" pool is the iter-var-
     * qualified field set (it.id, it.name, ...) plus unary openers and the
     * stdlib pool. Returns null when the cursor is NOT inside a lambda body.
     */
    private lambdaExpressionStartPool(ctx: CursorContext | null, cursor: number): CelSuggestion[] | null {
        if (ctx === null) return null
        for (const frame of this.lambdaIterFrames(ctx)) {
            const out: CelSuggestion[] = []
            for (const f of frame.fieldNames) {
                const display = `${frame.iterVar}.${f}`
                out.push(iterVarFieldSuggestion(display, {start: cursor, end: cursor, text: display}))
            }
            return appendUnaryOpeners(out)
        }
        return null
    }

    /**
     * Cursor at the end of a bare identifier or dotted path that doesn't end
     * in a dot — i.e. the user has typed a complete-or-partial name without
     * the trailing `.` or `(`. Two sub-cases:
     *   - exact match against an existing root/field/method → drop the
     *     echoed suggestion, surface `.` / `(` plus type-aware operators
     *   - partial prefix → narrow the expression-start pool, or surface
     *     after-dot fields for a path whose tail is a partial prefix
     */
    private exactMatchOrPartialPrefix(source: string, cursor: number, view: TokenView, ctx: CursorContext | null): CelSuggestion[] | null {
        if (view.hasTrailingWhitespace) return null
        if (view.lastNonWs === null || view.lastNonWs.kind !== 'IDENT') return null

        const {start: tokenStart, text: token} = identTokenBefore(source, cursor)
        if (token.length === 0) return null

        // NOTE: Cursor partial right after `&&` / `||` with optional whitespace —
        // pool narrowed by the partial. Inside a lambda body the pool is
        // iter-var-qualified.
        const beforeToken = source.substring(0, tokenStart).trimEnd()
        if (beforeToken.endsWith('&&') || beforeToken.endsWith('||')) {
            const lambdaPool = this.lambdaExpressionStartPool(ctx, tokenStart)
            const pool = lambdaPool ?? this.expressionStartPool()
            const exact = pool.find(s => s.text === token)
            if (exact !== undefined) {
                return this.exactMatchNextStep(exact, pool, null, token, null, source, cursor, tokenStart)
            }
            return spliceMatches(pool.filter(s => s.text.startsWith(token) || s.text.endsWith(`.${token}`)), tokenStart, cursor)
        }

        // Path completion: tokenStart preceded by `.`
        if (tokenStart > 0 && source.charAt(tokenStart - 1) === '.') {
            let pathStart = tokenStart - 1
            while (pathStart > 0 && (IDENT_CHAR_RE.test(source.charAt(pathStart - 1)) || source.charAt(pathStart - 1) === '.')) {
                pathStart--
            }
            const dottedParent = source.substring(pathStart, tokenStart - 1)
            if (dottedParent.length === 0) return null

            let type: TypeRef | null = null
            const parts = dottedParent.split('.')
            if (this.schemaField.registry().containsRoot(parts[0])) {
                type = this.schemaField.typeForPath(dottedParent)
            } else {
                const lambdaInfo = this.resolveIterVarPath(ctx, dottedParent)
                if (lambdaInfo === null) return null
                type = lambdaInfo.type
            }
            const all = this.fieldsFromType(dottedParent, type)
            if (all.length === 0) return null

            const exact = all.find(s => s.text === token)
            if (exact !== undefined) {
                return this.exactMatchNextStep(exact, all, dottedParent, token, type, source, cursor, tokenStart)
            }
            return spliceMatches(all.filter(s => s.text.startsWith(token)), tokenStart, cursor)
        }

        // Root-level token (no leading `.`).
        const pool = this.expressionStartPool()
        const exact = pool.find(s => s.text === token)
        if (exact !== undefined) {
            return this.exactMatchNextStep(exact, pool, null, token, null, source, cursor, tokenStart)
        }
        const filtered = pool.filter(s => s.text.startsWith(token))
        if (filtered.length === 0) return null
        return spliceMatches(filtered, tokenStart, cursor)
    }

    /**
     * Drop the echoed exact-match suggestion and surface the natural
     * next-step token plus type-aware operators for scalar fields. Mirrors
     * IamCelSuggestionEngine.applyExactMatchNextStep.
     */
    private exactMatchNextStep(
        exact: CelSuggestion, all: readonly CelSuggestion[],
        _operandPath: string | null, token: string, type: TypeRef | null,
        _source: string, cursor: number, tokenStart: number,
    ): CelSuggestion[] {
        const out: CelSuggestion[] = []
        const STDLIB_AS_FUNCS = new Set(['size', 'int', 'uint', 'double', 'string', 'bool', 'bytes', 'type', 'timestamp', 'duration', 'dyn', 'has', 'cel.bind'])
        let nextStep: string
        let nextStepDesc: string
        if (exact.kind === Kind.LIST_MACRO || exact.kind === Kind.STRING_METHOD) {
            nextStep = '('
            nextStepDesc = 'Open call parenthesis'
        } else if (exact.kind === Kind.ROOT_KEYWORD) {
            if (STDLIB_AS_FUNCS.has(exact.text)) {
                nextStep = '('
                nextStepDesc = 'Open call parenthesis'
            } else {
                nextStep = '.'
                nextStepDesc = 'Chain into the next segment'
            }
        } else if (exact.kind === Kind.STATIC_FIELD || exact.kind === Kind.ATTRIBUTE || exact.kind === Kind.RELATIONSHIP_ALIAS) {
            nextStep = '.'
            nextStepDesc = 'Chain into the next segment'
        } else {
            return []
        }
        out.push({
            text: nextStep, kind: Kind.CLOSER,
            valueType: null, description: nextStepDesc,
            replacement: {start: cursor, end: cursor, text: nextStep},
        })
        // For path fields with a step-into type, surface comparison operators.
        if (nextStep === '.' && type !== null) {
            const fieldType = this.schemaField.stepInto(type, token)
            const selectedPath = _operandPath === null || _operandPath.length === 0
                ? token
                : `${_operandPath}.${token}`
            const ranked = this.schemaField.registry().resolveRanked(selectedPath)
            const ops = operatorsForType(fieldType, ranked)
            for (const op of ops) {
                out.push({
                    text: op, kind: Kind.OPERATOR,
                    valueType: null, description: SYMBOL_DESCRIPTIONS.get(op) ?? null,
                    replacement: {start: cursor, end: cursor, text: ` ${op} `},
                })
            }
        }
        // Include remaining (non-exact) entries that share the prefix.
        for (const s of all) {
            if (s.text !== token && s.text.startsWith(token)) {
                out.push({...s, replacement: {start: tokenStart, end: cursor, text: s.text}})
            }
        }
        return out
    }

    private expressionStartPool(): CelSuggestion[] {
        const out: CelSuggestion[] = []
        for (const root of this.schemaField.rootNames()) {
            out.push(celSuggestion(root, Kind.ROOT_KEYWORD, null, ROOT_DESCRIPTIONS.get(root) ?? null))
        }
        for (const s of CEL_STDLIB) {
            out.push(celSuggestion(s, Kind.ROOT_KEYWORD, null, STDLIB_DESCRIPTIONS.get(s) ?? null))
        }
        for (const op of UNARY_OPENERS) {
            out.push(celSuggestion(op, Kind.LOGICAL, null, SYMBOL_DESCRIPTIONS.get(op) ?? null))
        }
        return out
    }
}

/**
 * Operators applicable to a resolved path type. Mirrors
 * IamCelSuggestionEngine.operatorsForType in Java.
 *
 * Issue 781: `ranked` widens the STRING/ENUM pool to include ordinal
 * operators (`<`, `<=`, `>`, `>=`) when the underlying type-table is
 * semantically ordered. Numeric/date types ignore `ranked` — their
 * operator pool is type-driven, not type-table-driven.
 */
function operatorsForType(type: TypeRef | null, ranked = false): readonly string[] {
    if (type === null) return []
    if (isList(type)) return ['==', '!=', 'in']
    if (isMap(type)) return ['==', '!=']
    if (isScalar(type)) {
        switch (type.fieldType) {
            case 'STRING':
            case 'ENUM':
                return ranked
                    ? ['==', '!=', '<', '<=', '>', '>=', 'in']
                    : ['==', '!=', 'in']
            case 'BOOLEAN':
                return ['==', '!=']
            case 'NUMBER':
                return ['==', '!=', '<', '<=', '>', '>=', 'in']
            case 'DYN':
                return ['==', '!=', '<', '<=', '>', '>=', 'in']
            default:
                return ['==', '!=']
        }
    }
    if (isStruct(type)) return ['==', '!=']
    return []
}

// NOTE: === Minimal token view ===
// PLATFORM SUBSTITUTION: cel-java uses ANTLR-generated CELLexer to get
// strongly-typed tokens. For the TS port we hand-write a CEL-flavoured
// tokenizer sufficient for the suggester's token-only fast paths. Refines as
// more cases need lexer knowledge.

const IDENT_RE = /[A-Za-z_][A-Za-z0-9_]*/
const NUM_RE = /\d+(?:\.\d+)?/

type TokenView = {
    readonly source: string
    readonly cursor: number
    readonly prefix: string
    readonly lastNonWs: Token | null
    readonly hasTrailingWhitespace: boolean
    readonly openParenDepth: number
    readonly openBracketDepth: number
    readonly cursorIsAfterValueWithOptionalOpChar: boolean
}

type Token = {
    readonly kind: 'IDENT' | 'NUM' | 'STRING' | 'PUNCT' | 'WS'
    readonly text: string
    readonly start: number
    readonly stop: number
}

function lexTokenView(source: string, cursor: number): TokenView {
    const prefix = source.slice(0, cursor)
    const tokens = tokenize(prefix)
    let lastNonWs: Token | null = null
    let parenDepth = 0
    let bracketDepth = 0
    for (const t of tokens) {
        if (t.kind !== 'WS') lastNonWs = t
        if (t.text === '(') parenDepth++
        else if (t.text === ')') parenDepth--
        else if (t.text === '[') bracketDepth++
        else if (t.text === ']') bracketDepth--
    }
    const hasTrailingWhitespace = prefix.length > 0 && /\s/.test(prefix.charAt(prefix.length - 1))
    let cursorIsAfterValueWithOptionalOpChar = false
    for (let i = tokens.length - 1; i >= 0; i--) {
        const t = tokens[i]
        if (t.kind === 'WS') continue
        if (t.kind === 'PUNCT' && (t.text === '!' || t.text === '<' || t.text === '>' || t.text === '=')) {
            continue
        }
        cursorIsAfterValueWithOptionalOpChar = t.kind === 'IDENT' || t.kind === 'STRING' || t.kind === 'NUM' || t.text === ')' || t.text === ']'
        break
    }
    return {source, cursor, prefix, lastNonWs, hasTrailingWhitespace, openParenDepth: parenDepth, openBracketDepth: bracketDepth, cursorIsAfterValueWithOptionalOpChar}
}

function tokenize(input: string): Token[] {
    const out: Token[] = []
    let i = 0
    while (i < input.length) {
        const c = input.charAt(i)
        if (/\s/.test(c)) {
            const start = i
            while (i < input.length && /\s/.test(input.charAt(i))) i++
            out.push({kind: 'WS', text: input.slice(start, i), start, stop: i - 1})
            continue
        }
        if (c === "'" || c === '"') {
            const start = i
            i++
            while (i < input.length && input.charAt(i) !== c) {
                if (input.charAt(i) === '\\' && i + 1 < input.length) i++
                i++
            }
            if (i < input.length) i++
            out.push({kind: 'STRING', text: input.slice(start, i), start, stop: i - 1})
            continue
        }
        const identMatch = IDENT_RE.exec(input.slice(i))
        if (identMatch !== null && identMatch.index === 0) {
            const start = i
            i += identMatch[0].length
            out.push({kind: 'IDENT', text: identMatch[0], start, stop: i - 1})
            continue
        }
        const numMatch = NUM_RE.exec(input.slice(i))
        if (numMatch !== null && numMatch.index === 0) {
            const start = i
            i += numMatch[0].length
            out.push({kind: 'NUM', text: numMatch[0], start, stop: i - 1})
            continue
        }
        out.push({kind: 'PUNCT', text: c, start: i, stop: i})
        i++
    }
    return out
}

// === Convenience top-level function used by the catalog runner ===

const DEFAULT_ENGINE = new IamCelSuggestionEngine()

export function suggest(source: string, cursor: number): CelSuggestion[] {
    return DEFAULT_ENGINE.suggest(source, cursor)
}
